import React, { useState, useMemo } from 'react';
import { useSoloTimetableStore, DEFAULT_TRANSITIONS, DOMAIN_COLORS, PERIODS } from '../../lib/stores/soloTimetableStore';
import { Icons } from '../../lib/Icons';

/**
 * SoloActivityPicker - Modal for selecting activities to add to timetable slots
 */
const SoloActivityPicker = ({ isOpen, onClose, day, periodKey }) => {
  const { availableActivities, setSlotActivity, clearSlot, getSlot } = useSoloTimetableStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customActivity, setCustomActivity] = useState({ title: '', description: '' });

  const currentSlot = getSlot(day, periodKey);
  const period = PERIODS.find(p => p.key === periodKey);
  const isTransitionSlot = period?.type === 'transition';

  // Filter activities based on search and domain
  const filteredActivities = useMemo(() => {
    let activities = isTransitionSlot ? DEFAULT_TRANSITIONS : availableActivities;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      activities = activities.filter(a =>
        a.title?.toLowerCase().includes(query) ||
        a.description?.toLowerCase().includes(query)
      );
    }

    if (selectedDomain && !isTransitionSlot) {
      activities = activities.filter(a =>
        a.targetDomains?.includes(selectedDomain)
      );
    }

    return activities;
  }, [availableActivities, searchQuery, selectedDomain, isTransitionSlot]);

  const handleSelectActivity = (activity) => {
    setSlotActivity(day, periodKey, activity);
    onClose();
  };

  const handleClearSlot = () => {
    clearSlot(day, periodKey);
    onClose();
  };

  const handleAddCustom = () => {
    if (!customActivity.title.trim()) return;

    const newActivity = {
      id: `custom-${Date.now()}`,
      title: customActivity.title.trim(),
      description: customActivity.description.trim(),
      isCustom: true,
      targetDomains: [],
      matchScore: 0
    };

    setSlotActivity(day, periodKey, newActivity);
    setCustomActivity({ title: '', description: '' });
    setShowCustomForm(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-slate-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden border border-slate-700">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <div>
            <h2 className="text-lg font-semibold text-white">
              {isTransitionSlot ? 'Select Transition Activity' : 'Select Activity'}
            </h2>
            <p className="text-sm text-slate-400">
              {day} - {period?.label} ({period?.time})
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition-colors"
          >
            <Icons.XMark className="w-5 h-5" />
          </button>
        </div>

        {/* Current Activity */}
        {currentSlot?.activity && (
          <div className="p-4 bg-slate-700/50 border-b border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 mb-1">Currently selected:</p>
                <p className="text-white font-medium">{currentSlot.activity.title}</p>
              </div>
              <button
                onClick={handleClearSlot}
                className="px-3 py-1.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
              >
                Remove
              </button>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        {!isTransitionSlot && (
          <div className="p-4 border-b border-slate-700 space-y-3">
            {/* Search */}
            <div className="relative">
              <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search activities..."
                className="w-full pl-10 pr-4 py-2 bg-slate-700 text-white placeholder-slate-400 rounded-xl border border-slate-600 focus:border-purple-500 focus:outline-none text-sm"
              />
            </div>

            {/* Domain Filters */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedDomain(null)}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${
                  selectedDomain === null
                    ? 'bg-purple-500 text-white'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                All
              </button>
              {Object.entries(DOMAIN_COLORS).slice(0, 4).map(([domain, colors]) => (
                <button
                  key={domain}
                  onClick={() => setSelectedDomain(domain === selectedDomain ? null : domain)}
                  className={`px-3 py-1 text-xs rounded-full transition-colors ${
                    selectedDomain === domain
                      ? `${colors.bg} ${colors.text} ${colors.border} border`
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  {domain}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Activity List */}
        <div className="overflow-y-auto max-h-[400px] p-4 space-y-2">
          {filteredActivities.length === 0 ? (
            <div className="text-center py-8">
              <Icons.MagnifyingGlass className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No activities found</p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-2 text-sm text-purple-400 hover:text-purple-300"
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            filteredActivities.map((activity) => {
              const primaryDomain = activity.targetDomains?.[0];
              const colors = DOMAIN_COLORS[primaryDomain] || DOMAIN_COLORS['Cognition'];
              const isSelected = currentSlot?.activity?.id === activity.id;

              return (
                <button
                  key={activity.id}
                  onClick={() => handleSelectActivity(activity)}
                  className={`w-full text-left p-3 rounded-xl border transition-all ${
                    isSelected
                      ? 'bg-purple-500/20 border-purple-500'
                      : 'bg-slate-700/50 border-slate-600 hover:bg-slate-700 hover:border-slate-500'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-medium truncate">{activity.title}</h4>
                      {activity.description && (
                        <p className="text-sm text-slate-400 line-clamp-2 mt-1">
                          {activity.description}
                        </p>
                      )}
                      {activity.targetDomains && activity.targetDomains.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {activity.targetDomains.slice(0, 3).map((domain, idx) => {
                            const domainColors = DOMAIN_COLORS[domain];
                            return (
                              <span
                                key={idx}
                                className={`px-2 py-0.5 text-xs rounded ${domainColors?.bg || 'bg-slate-600'} ${domainColors?.text || 'text-slate-300'}`}
                              >
                                {domain}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    {activity.matchScore > 0 && (
                      <span className="flex-shrink-0 px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-lg font-medium">
                        {Math.round(activity.matchScore * 100)}% match
                      </span>
                    )}
                    {isSelected && (
                      <Icons.Check className="flex-shrink-0 w-5 h-5 text-purple-400" />
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Custom Activity Form */}
        {showCustomForm ? (
          <div className="p-4 border-t border-slate-700 bg-slate-700/30">
            <h4 className="text-sm font-medium text-white mb-3">Add Custom Activity</h4>
            <div className="space-y-3">
              <input
                type="text"
                value={customActivity.title}
                onChange={(e) => setCustomActivity(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Activity title"
                className="w-full px-3 py-2 bg-slate-700 text-white placeholder-slate-400 rounded-lg border border-slate-600 focus:border-purple-500 focus:outline-none text-sm"
              />
              <textarea
                value={customActivity.description}
                onChange={(e) => setCustomActivity(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description (optional)"
                rows={2}
                className="w-full px-3 py-2 bg-slate-700 text-white placeholder-slate-400 rounded-lg border border-slate-600 focus:border-purple-500 focus:outline-none text-sm resize-none"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleAddCustom}
                  disabled={!customActivity.title.trim()}
                  className="flex-1 px-4 py-2 bg-purple-500 text-white text-sm font-medium rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add Activity
                </button>
                <button
                  onClick={() => {
                    setShowCustomForm(false);
                    setCustomActivity({ title: '', description: '' });
                  }}
                  className="px-4 py-2 bg-slate-600 text-white text-sm rounded-lg hover:bg-slate-500 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-4 border-t border-slate-700">
            <button
              onClick={() => setShowCustomForm(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-slate-600 text-slate-400 rounded-xl hover:border-purple-500 hover:text-purple-400 transition-colors"
            >
              <Icons.Plus className="w-4 h-4" />
              Add Custom Activity
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SoloActivityPicker;
